/**
 * Finance utilities — single source of truth for every number the site shows.
 * Keeping these in one file means changing the formula in exactly one place.
 */

/** Format naira with thousands separators and 2 dp. */
export function fmtNGN(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return sign + "₦" + abs.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Format naira with thousands separators, no kobo (whole-naira only). */
export function fmtNGNWhole(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const sign = n < 0 ? "-" : "";
  const abs = Math.round(Math.abs(n));
  return sign + "₦" + abs.toLocaleString("en-NG");
}

/** Round to 2 decimal places. */
export function round2(x) {
  return Math.round(x * 100) / 100;
}

/**
 * Paystack local-card processing charge (Nigeria):
 *   base = 1.5% of amount
 *   + ₦100 flat, waived when amount < ₦2,500
 *   capped at ₦2,000
 * Returns the charge Paystack adds on top of the amount.
 */
export function paystackFee(amount) {
  if (!amount || amount <= 0) return 0;
  const base = amount * 0.015;
  const withFlat = amount < 2500 ? base : base + 100;
  return round2(Math.min(withFlat, 2000));
}

/**
 * Standard equal-monthly-payment amortisation.
 *   principal  — loan amount (naira)
 *   aprPct     — nominal annual percentage rate (e.g. 30 for 30%)
 *   tenorMonths — integer number of monthly instalments
 * Returns { monthly, total, interest } in naira; monthly is rounded to whole naira
 * so the displayed total always equals monthly x months.
 */
export function amortize(principal, aprPct, tenorMonths) {
  if (!principal || !tenorMonths) return { monthly: 0, total: 0, interest: 0 };
  const r = (Number(aprPct) / 100) / 12;
  const n = Number(tenorMonths);
  const monthly = r > 0
    ? principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
    : principal / n;
  const whole = Math.round(monthly);
  const total = whole * n;
  return {
    monthly: whole,
    total,
    interest: round2(total - principal),
  };
}

/**
 * Late fee per missed instalment:
 *   pct% of the instalment amount, capped (if cap given) at capNGN.
 */
export function lateFeePerMiss(instalmentAmount, pct, capNGN) {
  const raw = (instalmentAmount || 0) * ((pct || 0) / 100);
  if (capNGN && raw > capNGN) return round2(capNGN);
  return round2(raw);
}

/**
 * APR including a one-time processing fee that is added to the loan (not deducted):
 * the borrower receives the full principal and pays the fee with the first instalment.
 * Solves the monthly IRR by Newton-Raphson and annualises (x 12). Percentage, 2 dp.
 */
export function aprIncludingFee(principal, aprPct, tenorMonths, feePct) {
  if (!principal || !tenorMonths) return aprPct;
  const fee = principal * (feePct || 0) / 100;
  const { monthly } = amortize(principal, aprPct, tenorMonths);
  const flows = Array.from({ length: tenorMonths }, (_, i) => monthly + (i === 0 ? fee : 0));
  let r = (aprPct / 100) / 12 || 0.01;
  for (let i = 0; i < 60; i++) {
    let pv = 0, dpv = 0;
    flows.forEach((cf, idx) => {
      const k = idx + 1;
      const f = Math.pow(1 + r, k);
      pv += cf / f;
      dpv += -k * cf / (f * (1 + r));
    });
    const g = pv - principal;
    if (Math.abs(g) < 0.001) break;
    r -= g / dpv;
    if (r < 0) r = 0.0001;
  }
  return round2(r * 12 * 100);
}
